import React, { useEffect, useState } from "react";
import ReusableTable from "components/Tables/ReusableTable";
import Skeleton from "react-loading-skeleton";
import "react-loading-skeleton/dist/skeleton.css";
import { Box, Flex, Badge, Text } from "@chakra-ui/react";
import { Eye, EllipsisVertical, Edit } from "lucide-react";
import ThreeDotMenu from "components/Menus/ThreeDotMenu";

import ViewClaimDrawer from "./components/ViewClaimDrawer";
import ReviewClaimDrawer from "./components/ReviewClaimDrawer";

import { useDispatch, useSelector } from "react-redux";
import { fetchClaimsAction } from "../../../state/actions/claimActions";

import { useCustomNotification } from "components/Toasts/OpenNotification";

const ManageClaimsTab = () => {
  const dispatch = useDispatch();
  const notify = useCustomNotification();

  const { claims = [], loading } = useSelector((state) => state.claims || {});

  const [isViewDrawerOpen, setViewDrawerOpen] = useState(false);
  const [isReviewDrawerOpen, setReviewDrawerOpen] = useState(false);
  const [selectedClaim, setSelectedClaim] = useState(null);

  useEffect(() => {
    // same style as Company Settings (simple fetch on mount)
    dispatch(fetchClaimsAction(1, 10));
  }, [dispatch]);

  const openViewDrawer = (claim) => {
    setSelectedClaim(claim);
    setViewDrawerOpen(true);
  };

  const openReviewDrawer = (claim) => {
    setSelectedClaim(claim);
    setReviewDrawerOpen(true);
  };

  const statusColor = (status) => {
    const s = (status || "").toLowerCase();
    if (s === "approved") return "green";
    if (s === "rejected") return "red";
    if (s === "submitted") return "blue";
    if (s === "review") return "yellow";
    return "gray";
  };

  const generateMenuItems = (row) => [
    {
      icon: <Eye size={16} />,
      text: "View Details",
      onClick: () => openViewDrawer(row.original),
    },
    {
      icon: <Edit size={16} />,
      text: "Review Claim",
      onClick: () => openReviewDrawer(row.original),
    },
  ];

  const columnsData = [
    { Header: "Package ID", accessor: "package.package_number" },
    { Header: "Reason", accessor: "reason" },
    {
      Header: "Status",
      accessor: "status",
      Cell: ({ value }) => (
        <Badge colorScheme={statusColor(value)} borderRadius="full" px={2}>
          {value || "N/A"}
        </Badge>
      ),
    },
    {
      Header: "Created",
      accessor: "created_at",
      Cell: ({ value }) => (
        <Text fontSize="sm">{value ? new Date(value).toLocaleString() : "N/A"}</Text>
      ),
    },
    {
      Header: "Actions",
      accessor: "actions",
      Cell: ({ row }) => (
        <ThreeDotMenu
          icon={<EllipsisVertical size={20} color="grey" />}
          items={generateMenuItems(row)}
        />
      ),
    },
  ];

  return (
    <Flex flexDirection="column" pt={{ base: "120px", md: "75px" }}>
       <Box p={6}>
              <Flex justifyContent="space-between" alignItems="center" mb={4}>
                {loading ? (
                  <Skeleton height="20px" width="120px" />
                ) : (
                  <Text fontSize="md" fontWeight="bold">
                    All Claims: {claims?.length ?? 0}
                  </Text>
                )}
              </Flex>
            </Box>
      <ReusableTable columnsData={columnsData} tableData={claims || []} isLoading={loading} />

      <ViewClaimDrawer
        isOpen={isViewDrawerOpen}
        onClose={() => setViewDrawerOpen(false)}
        claim={selectedClaim}
      />

      <ReviewClaimDrawer
        isOpen={isReviewDrawerOpen}
        onClose={() => setReviewDrawerOpen(false)}
        claim={selectedClaim}
        onReviewed={(status) => {
          notify({
            type: "success",
            message: "Updated",
            description: `Claim ${status} successfully.`,
          });
          dispatch(fetchClaimsAction(1, 10)); 
        }}
        onError={(msg) => {
          notify({
            type: "error",
            message: "Error",
            description: msg || "Failed to review claim.",
          });
        }}
      />
    </Flex>
  );
};

export default ManageClaimsTab;